/**
 * Company (BtoB) API Service
 * 組織管理APIとの通信
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';

class CompanyService {
  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api/company`,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    });
  }

  _getAuthHeaders() {
    const token = localStorage.getItem('struct-code-token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  _handleError(error) {
    if (error.response) {
      let detail = error.response.data.detail;
      // Pydantic validation errors return an array of objects — extract message strings
      if (Array.isArray(detail)) {
        detail = detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
      } else if (typeof detail === 'object' && detail !== null) {
        detail = detail.msg || JSON.stringify(detail);
      }
      return { message: detail || 'Server error', status: error.response.status };
    } else if (error.request) {
      return { message: 'Cannot connect to server', status: 0 };
    }
    return { message: error.message || 'Unexpected error', status: -1 };
  }

  // ── My Companies ──

  async getMyCompanies() {
    try {
      const res = await this.client.get('/my-companies', { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // ── Company CRUD ──

  async createCompany(name, departments = []) {
    try {
      const res = await this.client.post('/', { name, departments }, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getJoinPreview(inviteCode) {
    try {
      const res = await this.client.get(`/join/preview`, { params: { invite_code: inviteCode }, headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async joinCompany(inviteCode, displayName, { departmentId = null, teamId = null, position = null } = {}) {
    try {
      const payload = { invite_code: inviteCode, display_name: displayName };
      if (departmentId) payload.department_id = departmentId;
      if (teamId) payload.team_id = teamId;
      if (position) payload.position = position;
      const res = await this.client.post('/join', payload, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getCompany(slug) {
    try {
      const res = await this.client.get(`/${slug}`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async updateCompany(slug, data) {
    try {
      const res = await this.client.put(`/${slug}`, data, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async regenerateInviteCode(slug) {
    try {
      const res = await this.client.post(`/${slug}/invite-code`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // ── Positions ──

  async updatePositions(slug, positions) {
    try {
      const res = await this.client.put(`/${slug}/positions`, { positions }, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // ── Departments ──

  async createDepartment(slug, name) {
    try {
      const res = await this.client.post(`/${slug}/departments`, { name }, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async updateDepartment(slug, deptId, data) {
    try {
      const res = await this.client.put(`/${slug}/departments/${deptId}`, data, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async deleteDepartment(slug, deptId) {
    try {
      await this.client.delete(`/${slug}/departments/${deptId}`, { headers: this._getAuthHeaders() });
    } catch (e) { throw this._handleError(e); }
  }

  // ── Teams ──

  async createTeam(slug, name, departmentId) {
    try {
      const res = await this.client.post(`/${slug}/teams`, { name, department_id: departmentId }, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async updateTeam(slug, teamId, data) {
    try {
      const res = await this.client.put(`/${slug}/teams/${teamId}`, data, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async deleteTeam(slug, teamId) {
    try {
      await this.client.delete(`/${slug}/teams/${teamId}`, { headers: this._getAuthHeaders() });
    } catch (e) { throw this._handleError(e); }
  }

  // ── Members ──

  async getMembers(slug) {
    try {
      const res = await this.client.get(`/${slug}/members`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async updateMember(slug, memberId, data) {
    try {
      const res = await this.client.put(`/${slug}/members/${memberId}`, data, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async deleteMember(slug, memberId) {
    try {
      await this.client.delete(`/${slug}/members/${memberId}`, { headers: this._getAuthHeaders() });
    } catch (e) { throw this._handleError(e); }
  }

  async linkDiagnosis(slug, memberId, diagnosisId) {
    try {
      const res = await this.client.post(`/${slug}/members/${memberId}/link-diagnosis`, { diagnosis_id: diagnosisId }, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // ── Analysis ──

  async getAnalysis(slug) {
    try {
      const res = await this.client.get(`/${slug}/analysis`, { headers: this._getAuthHeaders() });
      return res.data?.analysis || res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getTeamAnalysis(slug, teamId) {
    try {
      const res = await this.client.get(`/${slug}/teams/${teamId}/analysis`, { headers: this._getAuthHeaders() });
      return res.data?.analysis || res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getDeptAnalysis(slug, deptId) {
    try {
      const res = await this.client.get(`/${slug}/departments/${deptId}/analysis`, { headers: this._getAuthHeaders() });
      return res.data?.analysis || res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // ── Position Analysis ──

  async getPositionAnalysis(slug, position) {
    try {
      const res = await this.client.get(`/${slug}/positions/analysis`, { params: { position }, headers: this._getAuthHeaders() });
      return res.data?.analysis || res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getCachedPositionAiAnalysis(slug, position) {
    try {
      const res = await this.client.get(`/${slug}/positions/ai-analysis`, { params: { position }, headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getPositionAiAnalysis(slug, position) {
    try {
      const res = await this.client.post(`/${slug}/positions/ai-analysis`, {}, { params: { position }, headers: this._getAuthHeaders(), timeout: 90000 });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // ── Reorder ──

  async reorderMembers(slug, memberIds) {
    try {
      const res = await this.client.put(`/${slug}/members/reorder`, { member_ids: memberIds }, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async reorderDepartments(slug, departmentIds) {
    try {
      const res = await this.client.put(`/${slug}/departments/reorder`, { department_ids: departmentIds }, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // ── AI Analysis ──

  async getCachedAiAnalysis(slug) {
    try {
      const res = await this.client.get(`/${slug}/ai-analysis`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getAiAnalysis(slug) {
    try {
      const res = await this.client.post(`/${slug}/ai-analysis`, {}, { headers: this._getAuthHeaders(), timeout: 90000 });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getCachedTeamAiAnalysis(slug, teamId) {
    try {
      const res = await this.client.get(`/${slug}/teams/${teamId}/ai-analysis`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getTeamAiAnalysis(slug, teamId) {
    try {
      const res = await this.client.post(`/${slug}/teams/${teamId}/ai-analysis`, {}, { headers: this._getAuthHeaders(), timeout: 90000 });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getCachedDeptAiAnalysis(slug, deptId) {
    try {
      const res = await this.client.get(`/${slug}/departments/${deptId}/ai-analysis`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getDeptAiAnalysis(slug, deptId) {
    try {
      const res = await this.client.post(`/${slug}/departments/${deptId}/ai-analysis`, {}, { headers: this._getAuthHeaders(), timeout: 90000 });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }
}

const companyService = new CompanyService();
export default companyService;
